import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import HeroSopralluogoMockup from "@/pages/landing/HeroSopralluogoMockup";
import HeroAgendaMockup from "@/pages/landing/HeroAgendaMockup";
import { Sparkle, Calendar, Check, MapPin, Clock, Eye, Heart, ChatCircle, Phone, Camera } from "@phosphor-icons/react";

/**
 * Mockup statico social-pack per la slide 3 — 4 tile in fila, tutti
 * mostrano la foto DOPO (sistemata da Pronto). Tile 1 e Tile 4 sono
 * marchiati REEL come deliverable video.
 */
function StaticSocialMockup() {
  const TILES = [
    { format: "reel", img: "/landing/sala-original-after.jpg", caption: "Reel · Living", rotate: -4 },
    { format: "post", img: "/landing/cucina-after.jpg", caption: "Post · Cucina", rotate: 2 },
    { format: "post", img: "/landing/bagno-after.jpg", caption: "Post · Bagno", rotate: -1 },
    { format: "reel", img: "/landing/rinnova-after.jpg", caption: "Reel · Restyling", rotate: 3 },
  ];
  return (
    <div className="flex items-center gap-3" style={{ width: 920 }}>
      {TILES.map((t, i) => (
        <div
          key={i}
          className="relative bg-white shadow-2xl"
          style={{
            width: 220, height: 275,
            transform: `rotate(${t.rotate}deg)`,
            borderRadius: 6, overflow: "hidden",
          }}
        >
          <img src={t.img} alt={t.caption} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.0) 60%, rgba(0,0,0,0.55) 100%)" }} />
          <span
            className="absolute top-2 left-2 text-[8.5px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5 rounded-sm"
            style={{
              color: "#FFFFFF",
              background: t.format === "reel" ? "#C8A66B" : "#1C1C1A",
            }}
          >
            {t.format === "reel" ? "REEL · 6s" : "POST 1:1"}
          </span>
          <div className="absolute bottom-2 left-2 right-2">
            <div className="text-[10px] text-white font-semibold leading-tight tracking-tight drop-shadow">{t.caption}</div>
            <div className="flex items-center gap-2 mt-1 text-white/80">
              <Heart size={9} weight="fill" />
              <ChatCircle size={9} weight="fill" />
              <Eye size={9} weight="fill" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Mini-card "annuncio pronto" arricchita: foto + dati + portali +
 * badge "23 lead" + thumbnail brochure piccola in alto a destra.
 */
function MiniListingCard() {
  return (
    <div className="bg-white rounded-md overflow-hidden shadow-2xl" style={{ width: 340 }}>
      <div className="relative">
        <img
          src="/landing/sala-original-after.jpg"
          alt="Trilocale Brera"
          style={{ width: "100%", height: 140, objectFit: "cover" }}
        />
        <span className="absolute top-2 left-2 text-[8.5px] uppercase tracking-[0.18em] text-white bg-[#5F6E54] px-1.5 py-0.5 font-bold">
          ANNUNCIO PUBBLICATO
        </span>
        <div className="absolute top-2 right-2 bg-white/95 px-1.5 py-0.5 rounded-sm">
          <span className="text-[8.5px] uppercase tracking-[0.16em] font-bold text-[#7C8B6E]">CLASSE B</span>
        </div>
        <span className="absolute bottom-2 left-2 text-[9px] uppercase tracking-[0.18em] text-white drop-shadow">
          1 / 12 foto
        </span>
      </div>
      <div className="p-3">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[8px] uppercase tracking-[0.18em] text-[#9A968B]">Trilocale</span>
          <span className="w-0.5 h-0.5 rounded-full bg-[#7C8B6E]" />
          <span className="text-[8px] uppercase tracking-[0.18em] text-[#7C8B6E] font-semibold">Brera</span>
        </div>
        <div className="font-editorial text-[16px] tracking-tight leading-tight text-[#1C1C1A]">
          Trilocale ristrutturato
        </div>
        <div className="font-editorial text-[12px] italic tracking-tight text-[#7C8B6E] -mt-0.5">
          in palazzo signorile.
        </div>
        <div className="flex items-baseline gap-2 mt-2">
          <span className="font-editorial text-[19px] text-[#7C8B6E] tracking-tight">€ 245.000</span>
          <span className="text-[10px] text-[#6B6A65]">· 95 m² · 3 locali</span>
        </div>
        <div className="mt-2.5 pt-2 border-t border-[#E5E7E0]">
          <div className="text-[7.5px] uppercase tracking-[0.18em] text-[#6B6A65] mb-1">Pronto per pubblicare su</div>
          <div className="flex flex-wrap gap-1">
            {["Idealista", "Immobiliare", "Casa.it", "Subito"].map((p) => (
              <span key={p} className="text-[8.5px] font-medium text-[#5F6E54] bg-[#F1F4ED] px-1.5 py-0.5 rounded-sm">{p}</span>
            ))}
          </div>
        </div>
        <div className="mt-2.5 flex items-center gap-2 text-[9px] text-[#6B6A65] uppercase tracking-[0.16em]">
          <Sparkle size={9} weight="fill" className="text-[#C8A66B]" />
          <span><strong className="text-[#1C1C1A]">23</strong> richieste</span>
          <span className="w-0.5 h-0.5 rounded-full bg-[#E5E7E0]" />
          <span><strong className="text-[#1C1C1A]">1.011</strong> visite</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Mini agenda card RICCA — settimana piena con 5 eventi colorati,
 * lista contatti collegati, sync indicator. Niente vuoto.
 */
function MiniAgendaCard() {
  return (
    <div className="bg-white rounded-md overflow-hidden shadow-2xl border border-[#E5E7E0]" style={{ width: 380 }}>
      <div className="px-3 pt-2.5 pb-2 flex items-center justify-between border-b border-[#E5E7E0]">
        <div>
          <div className="text-[8px] uppercase tracking-[0.16em] text-[#9A968B] font-bold">SETTIMANA · MAGGIO</div>
          <div className="font-editorial text-[15px] text-[#1C1C1A] tracking-tight leading-tight">12 — 16</div>
        </div>
        <div className="text-right">
          <div className="text-[8px] uppercase tracking-[0.16em] text-[#9A968B]">Impegni</div>
          <div className="font-editorial text-[15px] text-[#7C8B6E]">7 visite</div>
        </div>
      </div>
      <div className="grid grid-cols-5 text-[8.5px] uppercase tracking-[0.14em] font-bold text-[#9A968B] border-b border-[#E5E7E0]">
        {["Lun 12", "Mar 13", "Mer 14", "Gio 15", "Ven 16"].map((d, i) => (
          <div key={d} className="px-1 py-1.5 text-center" style={{ background: i === 1 ? "#F1F4ED" : "transparent" }}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-5 relative" style={{ height: 138 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="border-r border-[#E5E7E0] last:border-r-0 px-1 py-1 text-[7.5px] text-[#9A968B]">
            {i === 0 ? <><div>09:00</div><div className="mt-7">11:00</div><div className="mt-6">15:00</div></> : ""}
          </div>
        ))}
        {/* Evento Lun 09:30 */}
        <div className="absolute bg-white border-2 border-[#7C8B6E] text-[#1C1C1A] rounded px-1.5 py-0.5 shadow"
          style={{ top: 8, left: "calc(0% + 22px)", width: "calc(20% - 24px)" }}>
          <div className="text-[7px] font-bold uppercase tracking-[0.1em] text-[#5F6E54]">09:30</div>
          <div className="text-[8.5px] font-semibold leading-tight">Open House</div>
          <div className="text-[7.5px] leading-tight text-[#6B6A65]">Brera</div>
        </div>
        {/* Evento Mar 10:00 PRINCIPALE */}
        <div className="absolute bg-[#7C8B6E] text-white rounded px-1.5 py-1 shadow"
          style={{ top: 8, left: "calc(20% + 4px)", width: "calc(20% - 8px)" }}>
          <div className="flex items-center gap-1 text-[7.5px] font-bold uppercase tracking-[0.1em]">
            <Clock size={6} weight="fill" /> 10:00
          </div>
          <div className="text-[8.5px] font-semibold leading-tight mt-0.5">Sopralluogo</div>
          <div className="text-[8px] leading-tight opacity-90">Ferrari · Brera</div>
          <div className="text-[7px] mt-0.5 opacity-75">+ vocale 1m12</div>
        </div>
        {/* Evento Mar pomeriggio */}
        <div className="absolute bg-[#F1F4ED] border border-[#7C8B6E]/40 text-[#1C1C1A] rounded px-1.5 py-0.5"
          style={{ top: 88, left: "calc(20% + 4px)", width: "calc(20% - 8px)" }}>
          <div className="text-[7px] font-bold uppercase tracking-[0.1em] text-[#5F6E54]">15:30</div>
          <div className="text-[8px] font-semibold leading-tight">Stima</div>
        </div>
        {/* Evento Mer */}
        <div className="absolute bg-white border-2 border-[#C8A66B] text-[#1C1C1A] rounded px-1.5 py-0.5 shadow"
          style={{ top: 28, left: "calc(40% + 4px)", width: "calc(20% - 8px)" }}>
          <div className="text-[7px] font-bold uppercase tracking-[0.1em] text-[#9C7E3D]">11:30</div>
          <div className="text-[8px] font-semibold leading-tight">Firma</div>
          <div className="text-[7.5px] leading-tight text-[#6B6A65]">Bianchi</div>
        </div>
        {/* Evento Gio */}
        <div className="absolute bg-white border-2 border-[#7C8B6E] text-[#1C1C1A] rounded px-1.5 py-0.5 shadow"
          style={{ top: 60, left: "calc(60% + 4px)", width: "calc(20% - 8px)" }}>
          <div className="text-[7px] font-bold uppercase tracking-[0.1em] text-[#5F6E54]">16:30</div>
          <div className="text-[8px] font-semibold leading-tight">Visita</div>
          <div className="text-[7.5px] leading-tight text-[#6B6A65]">Verdi</div>
        </div>
        {/* Evento Ven */}
        <div className="absolute bg-[#C8A66B]/15 border border-[#C8A66B]/50 text-[#1C1C1A] rounded px-1.5 py-0.5"
          style={{ top: 24, left: "calc(80% + 4px)", width: "calc(20% - 8px)" }}>
          <div className="text-[7px] font-bold uppercase tracking-[0.1em] text-[#9C7E3D]">10:00</div>
          <div className="text-[8px] font-semibold leading-tight">Promemoria</div>
          <div className="text-[7.5px] leading-tight">Anniversario</div>
        </div>
      </div>
      {/* Contatti collegati */}
      <div className="border-t border-[#E5E7E0] px-2.5 py-2">
        <div className="text-[7.5px] uppercase tracking-[0.16em] text-[#6B6A65] mb-1.5 font-bold">Contatti che cercano in questa zona</div>
        <div className="flex flex-wrap gap-1">
          {[
            { n: "Ferrari", t: "trilocale 200-300k" },
            { n: "Bianchi", t: "monolocale studenti" },
            { n: "Verdi", t: "casa giardino" },
          ].map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[8px] bg-[#F5F4F0] border border-[#E5E7E0] rounded-full px-1.5 py-0.5">
              <span className="font-semibold text-[#1C1C1A]">{c.n}</span>
              <span className="text-[#6B6A65]">{c.t}</span>
            </span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-t border-[#E5E7E0] bg-[#F1F4ED]/60">
        <Check size={11} weight="bold" className="text-[#5C6E50]" />
        <span className="text-[9px] text-[#5C6E50] font-semibold">Sincronizzato con Google Calendar e iCloud</span>
      </div>
    </div>
  );
}

const SLIDES = [
  {
    id: "1",
    eyebrow: "Sopralluogo Live",
    bgImage: "/landing/sala-original-after.jpg",
    component: HeroSopralluogoMockup,
    extraComponent: MiniListingCard,
    extraPosition: "bottom-right",
    claim: "Le case le vendi tu.",
    claimItalic: "Pronto fa il resto.",
    micro: "Tieni premuto, parli con il proprietario, scatti qualche foto. In 5 minuti hai annuncio scritto, foto sistemate, pianta arredata, pagina annuncio col QR e testi pronti per Idealista e Immobiliare.it. Tu controlli, pubblichi, vendi.",
  },
  {
    id: "3",
    eyebrow: "Instagram · Facebook · WhatsApp",
    bgImage: "/landing/cucina-after.jpg",
    component: StaticSocialMockup,
    claim: "Post, storia, reel.",
    claimItalic: "Tre formati pronti.",
    micro: "Per ogni immobile generi 2 reel da 6 secondi (Living e Restyling) e 2 post 1080×1080 (Cucina e Bagno). Pronto sistema le foto, monta i video, scrive le caption. Tu pubblichi e basta. Niente Canva, niente notti in piedi.",
  },
  {
    id: "4",
    eyebrow: "Agenda · Contatti · Match",
    bgImage: "/landing/bagno-after.jpg",
    component: HeroAgendaMockup,
    extraComponent: MiniAgendaCard,
    extraPosition: "bottom-right",
    claim: "Visite, scadenze, clienti.",
    claimItalic: "Pronto te li ricorda.",
    micro: "Detti una nota vocale dopo ogni telefonata: Pronto crea l'evento in agenda, salva il contatto con le sue preferenze, ti propone gli immobili giusti. 7 visite a settimana, zero dimenticanze. Sincronizzato con Google Calendar e iCloud.",
  },
];

export default function OgShowcase() {
  const [params] = useSearchParams();
  const slideParam = params.get("slide") || "1";
  const slide = useMemo(
    () => SLIDES.find((s) => s.id === slideParam) || SLIDES[0],
    [slideParam],
  );
  const Mockup = slide.component;
  const Extra = slide.extraComponent;
  const hasExtra = Boolean(Extra);

  return (
    <div
      style={{ width: 1080, height: 1080 }}
      className="relative overflow-hidden bg-[#1C1C1A]"
      data-testid={`og-showcase-${slide.id}`}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${slide.bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "brightness(0.95) saturate(0.85)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(241,244,237,0.92) 0%, rgba(241,244,237,0.78) 30%, rgba(241,244,237,0.45) 55%, rgba(28,28,26,0.55) 88%, rgba(28,28,26,0.85) 100%)",
        }}
      />
      <div
        className="absolute right-0 top-0 h-full"
        style={{
          width: 6,
          background: "linear-gradient(180deg, #C8A66B 0%, #5F6E54 50%, #1C1C1A 100%)",
        }}
      />

      <div className="relative pt-12 px-14 flex items-center justify-between z-20">
        <img
          src="/logo/full-with-tagline.png?v=2"
          alt="Pronto"
          style={{ height: 70 }}
          className="object-contain drop-shadow-sm"
        />
        <div className="flex flex-col items-end gap-2">
          <span
            className="text-[11px] uppercase font-semibold text-[#5F6E54]"
            style={{ letterSpacing: "0.28em" }}
          >
            {slide.eyebrow}
          </span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-white bg-[#1C1C1A] px-3 py-1.5 font-bold rounded-sm">
            GRATIS
          </span>
        </div>
      </div>

      {/* Mockup centerpiece — leggermente spostato a sx se c'è extra */}
      <div
        className="absolute z-10 flex items-center justify-center"
        style={{
          left: hasExtra ? "32%" : "50%",
          top: "47%",
          transform: `translate(-50%, -50%) scale(${hasExtra ? 0.95 : 1.18})`,
          filter: "drop-shadow(0 32px 56px rgba(28,28,26,0.35)) drop-shadow(0 12px 24px rgba(28,28,26,0.18))",
        }}
      >
        <Mockup />
      </div>

      {/* Extra deliverable card — bottom-right */}
      {hasExtra && (
        <div
          className="absolute z-10"
          style={{
            right: 80,
            top: "52%",
            transform: "translateY(-50%) rotate(2deg)",
            filter: "drop-shadow(0 24px 40px rgba(28,28,26,0.35)) drop-shadow(0 8px 16px rgba(28,28,26,0.2))",
          }}
        >
          <Extra />
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 px-14 pb-12 z-20">
        <div className="max-w-[860px]">
          <h2
            className="font-editorial text-white leading-[0.95] tracking-tighter"
            style={{
              fontSize: 64,
              textShadow: "0 2px 12px rgba(0,0,0,0.35)",
            }}
          >
            {slide.claim}{" "}
            <span className="italic" style={{ color: "#E5C887" }}>
              {slide.claimItalic}
            </span>
          </h2>
          <div className="flex items-center gap-3 mt-4">
            <div style={{ width: 60, height: 1.5, background: "#C8A66B" }} />
            <p
              className="text-white/85"
              style={{ fontSize: 15, lineHeight: 1.5, maxWidth: 720 }}
            >
              {slide.micro}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
